import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import { WeekFilter } from '../ui/week-filter';
import { matchesWeek } from '../../lib/weekFilter';
import i18n from '../../i18n';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Plus, Download, Trash2, Edit, ChevronDown, ChevronRight, Printer, Zap, Shield, Activity, Search, FileSignature, CheckCircle, AlertCircle, UserCog, LayoutGrid, List, Camera, Package } from 'lucide-react';
import { toast } from 'sonner';
import TestReportFillForm from './TestReportFillForm';
import ReassignOwnerDialog from './ReassignOwnerDialog';
import usePermissions from '../../hooks/usePermissions';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_BASE}/api`;

const TYPE_META = {
  continuity: { label: i18n.t('auto.lbl.earthContinuity'), icon: Activity, color: 'bg-blue-50 text-blue-700 border-blue-200', prefix: 'EC' },
  insulation: { label: i18n.t('auto.lbl.insulationResistance'), icon: Shield, color: 'bg-purple-50 text-purple-700 border-purple-200', prefix: 'IR' },
  fault_loop: { label: i18n.t('auto.lbl.earthFaultLoop'), icon: Zap, color: 'bg-orange-50 text-orange-700 border-orange-200', prefix: 'FL' },
  rcd: { label: i18n.t('auto.lbl.rcdTest'), icon: Zap, color: 'bg-red-50 text-red-700 border-red-200', prefix: 'RCD' },
  torque: { label: i18n.t('auto.lbl.torqueTightening'), icon: Activity, color: 'bg-amber-50 text-amber-700 border-amber-200', prefix: 'TQ' },
};

const STATUS_META = {
  draft: { label: i18n.t('auto.lbl.draft'), color: 'bg-gray-100 text-gray-700' },
  in_progress: { label: i18n.t('auto.lbl.inProgress'), color: 'bg-blue-100 text-blue-700' },
  completed: { label: i18n.t('auto.lbl.approved'), color: 'bg-green-100 text-green-700' },
  conditional_approval: { label: i18n.t('auto.lbl.conditional'), color: 'bg-orange-100 text-orange-700' },
  signed_off: { label: i18n.t('auto.lbl.signedOff'), color: 'bg-emerald-100 text-emerald-700' },
};

const ElectricalTestReports = ({ token, projects = [], currentUser }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [instances, setInstances] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterOwner, setFilterOwner] = useState('all');
  const [groupByPanel, setGroupByPanel] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState({});
  const [reassignTarget, setReassignTarget] = useState(null);
  const [combinedDialog, setCombinedDialog] = useState(null);  // { group, mono, signedOnly }
  const [packDialog, setPackDialog] = useState(null);  // { project_id, project_name, mono, signedOnly, includeAppendix, handover }
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createType, setCreateType] = useState('continuity');
  const [createTplId, setCreateTplId] = useState('');
  const [createProjectId, setCreateProjectId] = useState('');
  const [activeInstance, setActiveInstance] = useState(null);

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, insts] = await Promise.all([
        axios.get(`${API}/test-reports/templates`, authHeader),
        axios.get(`${API}/test-reports/instances`, authHeader),
      ]);
      setTemplates(tpls.data);
      setInstances(insts.data);
    } catch (e) {
      toast.error(t('auto.iso.failedToLoadTestReports'));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Templates filtered by chosen create type
  const templatesForType = useMemo(
    () => templates.filter(t => t.test_type === createType && t.is_active !== false),
    [templates, createType]
  );

  // Default selected template when type changes
  useEffect(() => {
    if (templatesForType.length && !templatesForType.find(t => t.id === createTplId)) {
      setCreateTplId(templatesForType[0].id);
    }
  }, [templatesForType, createTplId]);

  const filtered = useMemo(() => {
    return instances.filter(i => {
      if (filterType !== 'all' && i.test_type !== filterType) return false;
      if (filterStatus !== 'all' && i.status !== filterStatus) return false;
      if (filterProject !== 'all' && i.project_id !== filterProject) return false;
      if (filterOwner === 'mine' && i.created_by !== currentUser?.id) return false;
      if (filterOwner !== 'all' && filterOwner !== 'mine' && i.created_by !== filterOwner) return false;
      if (!matchesWeek(weekFilter, i.header_values?.date_of_test || i.created_at)) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const hay = `${i.report_number} ${i.template_name} ${i.project_name || ''} ${i.created_by_name || ''} ${i.header_values?.machine_panel_id || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [instances, filterType, filterStatus, filterProject, filterOwner, currentUser, searchTerm, weekFilter]);

  // Distinct owners across the loaded instances (for the dropdown)
  const owners = useMemo(() => {
    const map = new Map();
    instances.forEach(i => {
      if (i.created_by && i.created_by_name) map.set(i.created_by, i.created_by_name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [instances]);

  // When "Group by Machine / Panel" is on, bucket the filtered list by
  // (project_id || '∅', panel_id || '∅') so testers can see at a glance
  // whether a panel has the full EN 60204-1 set (EC + IR + FL).
  const grouped = useMemo(() => {
    if (!groupByPanel) return null;
    const buckets = new Map();
    filtered.forEach(i => {
      const panel = i.header_values?.machine_panel_id || '— No panel ID —';
      const proj = i.project_name || '— No project —';
      const key = `${proj}::${panel}`;
      if (!buckets.has(key)) {
        buckets.set(key, { project: proj, panel, items: [] });
      }
      buckets.get(key).items.push(i);
    });
    return Array.from(buckets.entries()).map(([key, group]) => {
      const types = new Set(group.items.map(g => g.test_type));
      return {
        key,
        project: group.project,
        panel: group.panel,
        items: group.items,
        completeness: ['continuity', 'insulation', 'fault_loop', 'rcd', 'torque'].map(t => ({
          type: t,
          present: types.has(t),
          count: group.items.filter(g => g.test_type === t).length,
        })),
      };
    }).sort((a, b) => a.project.localeCompare(b.project) || a.panel.localeCompare(b.panel));
  }, [filtered, groupByPanel]);

  const handleCombinedCertificate = async (group, { mono = false, signedOnly = false } = {}) => {
    try {
      const ids = group.items.map(i => i.id).join(',');
      const params = new URLSearchParams({ ids });
      if (mono) params.set('mono', 'true');
      if (signedOnly) params.set('signed_only', 'true');
      const url = `${API}/test-reports/combined/pdf?${params.toString()}`;
      const resp = await axios.get(url, { ...authHeader, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safePanel = String(group.panel || 'panel').replace(/[^\w-]+/g, '_');
      const suffix = `${signedOnly ? '_signed' : ''}${mono ? '_BW' : ''}`;
      link.download = weekFileName(`Combined_Test_Certificate_${safePanel}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Combined certificate exported`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to generate combined certificate';
      // Backend may have returned the error as a Blob — try to read it.
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const json = JSON.parse(txt);
          toast.error(json.detail || msg);
          return;
        } catch { /* fall through */ }
      }
      toast.error(msg);
    }
  };

  const handleCommissioningPack = async ({ project_id, project_name, mono = false, signedOnly = false, includeAppendix = false, handover = false } = {}) => {
    if (!project_id) { toast.error(t('auto.iso.pickAProjectFirst')); return; }
    try {
      toast.info(t('auto.iso.generatingCommissioningPack'));
      const params = new URLSearchParams({ project_id });
      if (mono) params.set('mono', 'true');
      if (signedOnly) params.set('signed_only', 'true');
      if (includeAppendix) params.set('include_appendix', 'true');
      if (handover) params.set('handover', 'true');
      const url = `${API}/test-reports/combined/pdf?${params.toString()}`;
      const resp = await axios.get(url, { ...authHeader, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safe = String(project_name || project_id).replace(/[^\w-]+/g, '_');
      const suffix = `${signedOnly ? '_signed' : ''}${mono ? '_BW' : ''}`;
      link.download = weekFileName(`Commissioning_Pack_${safe}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(t('auto.iso.commissioningPackDownloaded'));
    } catch (e) {
      let msg = e.response?.data?.detail || 'Failed to generate Commissioning Pack';
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const json = JSON.parse(txt);
          msg = json.detail || msg;
        } catch { /* keep msg */ }
      }
      toast.error(msg);
    }
  };

  const togglePanel = (key) => setExpandedPanels(prev => ({ ...prev, [key]: !prev[key] }));

  const handleCreate = async () => {
    if (!createTplId) { toast.error(t('auto.iso.pickATemplate')); return; }
    try {
      const resp = await axios.post(`${API}/test-reports/instances`, {
        template_id: createTplId,
        project_id: createProjectId || null,
      }, authHeader);
      toast.success(`Report ${resp.data.report_number} created`);
      setCreateDialogOpen(false);
      await fetchData();
      setActiveInstance(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm(t('auto.iso.deleteThisTestReportThisCannot'))) return;
    try {
      await axios.delete(`${API}/test-reports/instances/${id}`, authHeader);
      toast.success(t('auto.iso.reportDeleted'));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleExportPDF = async (id, mono = false, withPhotos = false, e) => {
    e?.stopPropagation?.();
    toast.info(withPhotos ? 'Generating PDF with photos…' : (mono ? t('auto.iso.generatingBWPdf') : t('auto.iso.generatingPdf')));
    try {
      const params = new URLSearchParams();
      if (mono) params.append('mono', 'true');
      if (withPhotos) params.append('with_photos', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await fetch(`${API}/test-reports/instances/${id}/pdf${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('PDF failed');
      const blob = await resp.blob();
      const disposition = resp.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `test_report_${id}${mono ? '_BW' : ''}${withPhotos ? '_with_photos' : ''}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = weekFileName(filename); a.click();
      URL.revokeObjectURL(url);
      toast.success(t('auto.iso.pdfDownloaded'));
    } catch (err) { toast.error(err.message); }
  };

  // If an instance is open, show the fill form
  if (activeInstance) {
    return (
      <TestReportFillForm
        instance={activeInstance}
        template={templates.find(t => t.id === activeInstance.template_id)}
        token={token}
        currentUser={currentUser}
        projects={projects}
        onBack={() => { setActiveInstance(null); fetchData(); }}
        onExportPDF={(mono, withPhotos) => handleExportPDF(activeInstance.id, mono, withPhotos)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="electrical-test-reports">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="h-5 w-5 text-orange-500" />
                {t('auto.iso.electricalTestReports')}
              </CardTitle>
              <CardDescription className="text-xs">
                EN 60204-1 test records — Earth Continuity, Insulation Resistance, Fault Loop Impedance
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {filterProject !== 'all' && (() => {
                const proj = projects.find(p => p.id === filterProject);
                const projHasReports = filtered.some(i => i.project_id === filterProject);
                return (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!projHasReports}
                    onClick={() => setPackDialog({
                      project_id: filterProject,
                      project_name: proj?.name || filterProject,
                      mono: false,
                      signedOnly: false,
                      includeAppendix: false,
                      handover: true,
                    })}
                    data-testid="commissioning-pack-btn"
                    title={projHasReports ? t('auto.iso.compileEveryTestReportForThis') : t('auto.iso.noReportsYetForThisProject')}
                  >
                    <Package className="h-4 w-4 mr-1" /> {t('auto.iso.commissioningPack')}
                  </Button>
                );
              })()}
              {can('iso_test_reports.create') && (
                <Button size="sm" onClick={() => setCreateDialogOpen(true)} data-testid="new-test-report-btn">
                  <Plus className="h-4 w-4 mr-1" /> {t('auto.iso.newReport')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input
                className="pl-8 h-9"
                placeholder={t('auto.iso.searchNumberTemplateOrProject')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-reports-input"
              />
            </div>
            <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="test-reports-week-filter" />
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('auto.iso.allTypes2')}</SelectItem>
                <SelectItem value="continuity">{t('auto.iso.earthContinuity')}</SelectItem>
                <SelectItem value="insulation">{t('auto.iso.insulationResistance')}</SelectItem>
                <SelectItem value="fault_loop">{t('auto.iso.faultLoop')}</SelectItem>
                <SelectItem value="rcd">{t('auto.iso.rcdTest')}</SelectItem>
                <SelectItem value="torque">{t('auto.iso.torqueTightening')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('auto.iso.allStatus2')}</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="w-[180px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('auto.iso.allProjects')}</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterOwner} onValueChange={setFilterOwner}>
              <SelectTrigger className="w-[160px] h-9" data-testid="filter-owner-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('auto.iso.allOwners')}</SelectItem>
                {currentUser && <SelectItem value="mine">{t('auto.iso.myReportsOnly')}</SelectItem>}
                {owners.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-1 border rounded-md p-0.5 bg-gray-50">
              <button
                type="button"
                onClick={() => setGroupByPanel(false)}
                className={`px-2 py-1 rounded flex items-center gap-1 text-xs transition ${!groupByPanel ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="view-mode-list"
                title={t('auto.iso.flatListView')}
              >
                <List className="h-3.5 w-3.5" /> {t('auto.iso.list')}
              </button>
              <button
                type="button"
                onClick={() => setGroupByPanel(true)}
                className={`px-2 py-1 rounded flex items-center gap-1 text-xs transition ${groupByPanel ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="view-mode-grouped"
                title={t('auto.iso.groupByMachinePanel')}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> {t('auto.iso.byPanel')}
              </button>
            </div>
          </div>

          {/* Summary counts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {['continuity','insulation','fault_loop','rcd','torque'].map(tt => {
              const meta = TYPE_META[tt];
              const Icon = meta.icon;
              const count = instances.filter(i => i.test_type === tt).length;
              return (
                <div key={tt} className={`p-2.5 rounded-lg border ${meta.color}`}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="text-xs font-medium">{meta.label}</span>
                  </div>
                  <div className="text-xl font-semibold mt-0.5">{count}</div>
                </div>
              );
            })}
            <div className="p-2.5 rounded-lg border bg-gray-50 text-gray-700 border-gray-200">
              <div className="text-xs font-medium">{t('auto.iso.total')}</div>
              <div className="text-xl font-semibold mt-0.5">{instances.length}</div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-10 text-gray-400 text-sm">{t('auto.iso.loading2')}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              No reports yet. Click <strong>{t('auto.iso.newReport')}</strong> to create one.
            </div>
          ) : groupByPanel ? (
            <div className="space-y-2">
              {grouped.map(g => {
                const isOpen = !!expandedPanels[g.key];
                const completeCount = g.completeness.filter(c => c.present).length;
                const isComplete = completeCount === 3;
                return (
                  <div key={g.key} className="border rounded-lg overflow-hidden bg-white">
                    <div className="flex items-center w-full px-3 py-2 hover:bg-gray-50 transition">
                      <button
                        type="button"
                        onClick={() => togglePanel(g.key)}
                        className="flex-1 flex items-center gap-3 text-left min-w-0"
                        data-testid={`panel-group-${g.key}`}
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            <span className="text-gray-500">{g.project}</span>
                            <span className="text-gray-300 mx-2">·</span>
                            <span className="font-mono">{g.panel}</span>
                          </div>
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {g.items.length} report{g.items.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 mr-2">
                          {g.completeness.map(c => {
                            const cm = TYPE_META[c.type];
                            const CIcon = cm.icon;
                            return (
                              <span
                                key={c.type}
                                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${c.present ? cm.color : 'bg-gray-50 text-gray-300 border-gray-200'}`}
                                title={`${cm.label}: ${c.present ? `${c.count} report(s)` : 'missing'}`}
                              >
                                <CIcon className="h-3 w-3" />
                                {c.present ? `×${c.count}` : '—'}
                              </span>
                            );
                          })}
                        </div>
                        <Badge className={`text-xs font-normal ${isComplete ? 'bg-green-100 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                          {isComplete ? 'EN 60204-1 set complete' : `${completeCount}/3 types`}
                        </Badge>
                      </button>
                      <Button
                        variant={isComplete ? 'default' : 'outline'}
                        size="sm"
                        className={`ml-3 h-7 text-xs gap-1 ${isComplete ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setCombinedDialog({ group: g, mono: false, signedOnly: false }); }}
                        title={t('auto.iso.generateCombinedCertificate')}
                        data-testid={`combined-cert-${g.key}`}
                      >
                        <FileSignature className="h-3.5 w-3.5" />
                        {t('auto.iso.combinedCertificate')}
                      </Button>
                    </div>
                    {isOpen && (
                      <div className="border-t bg-gray-50/40">
                        <table className="w-full text-sm">
                          <tbody>
                            {g.items.map(i => {
                              const meta = TYPE_META[i.test_type] || TYPE_META.continuity;
                              const sMeta = STATUS_META[i.status] || STATUS_META.draft;
                              const Icon = meta.icon;
                              return (
                                <tr
                                  key={i.id}
                                  className="border-t hover:bg-white cursor-pointer"
                                  onClick={() => setActiveInstance(i)}
                                  data-testid={`grouped-row-${i.id}`}
                                >
                                  <td className="px-3 py-2 font-mono text-xs font-semibold w-[110px]">{i.report_number}</td>
                                  <td className="px-3 py-2 w-[170px]">
                                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${meta.color}`}>
                                      <Icon className="h-3 w-3" /> {meta.label}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-gray-700 text-xs">
                                    <span className={i.created_by === currentUser?.id ? 'font-medium text-blue-700' : ''}>
                                      {i.created_by_name || '—'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-gray-500 text-xs">{(i.rows || []).length} rows</td>
                                  <td className="px-3 py-2"><Badge className={`${sMeta.color} text-xs font-normal`}>{sMeta.label}</Badge></td>
                                  <td className="px-3 py-2 text-gray-500 text-xs">{String(i.created_at || '').slice(0, 10)}</td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="inline-flex items-center gap-0.5">
                                      <Button variant="ghost" size="sm" onClick={(e) => handleExportPDF(i.id, false, e)} title={t('auto.iso.colorPdf')}>
                                        <Download className="h-3.5 w-3.5" />
                                      </Button>
                                      {can('iso_test_reports.unlock_signed') && (
                                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setReassignTarget(i); }} title={t('auto.iso.reassignOwner')}>
                                          <UserCog className="h-3.5 w-3.5 text-amber-600" />
                                        </Button>
                                      )}
                                      {can('iso_test_reports.delete') && (
                                        <Button variant="ghost" size="sm" onClick={(e) => handleDelete(i.id, e)} title={t('auto.iso.delete')}>
                                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
              {!grouped.length && (
                <div className="text-center py-8 text-sm text-gray-500">{t('auto.iso.noReportsMatchTheCurrentFilters')}</div>
              )}
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">{t('auto.iso.number')}</th>
                    <th className="text-left px-3 py-2">{t('auto.iso.type')}</th>
                    <th className="text-left px-3 py-2">{t('auto.iso.project')}</th>
                    <th className="text-left px-3 py-2">{t('auto.iso.machinePanel')}</th>
                    <th className="text-left px-3 py-2">{t('auto.iso.owner')}</th>
                    <th className="text-left px-3 py-2">{t('auto.iso.rows')}</th>
                    <th className="text-left px-3 py-2">{t('auto.iso.status')}</th>
                    <th className="text-left px-3 py-2">{t('auto.iso.created')}</th>
                    <th className="text-right px-3 py-2 w-[160px]">{t('auto.iso.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(i => {
                    const meta = TYPE_META[i.test_type] || TYPE_META.continuity;
                    const sMeta = STATUS_META[i.status] || STATUS_META.draft;
                    const Icon = meta.icon;
                    return (
                      <tr
                        key={i.id}
                        className="border-t hover:bg-gray-50 cursor-pointer"
                        onClick={() => setActiveInstance(i)}
                        data-testid={`test-report-row-${i.id}`}
                      >
                        <td className="px-3 py-2 font-mono text-xs font-semibold">{i.report_number}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${meta.color}`}>
                            <Icon className="h-3 w-3" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{i.project_name || <span className="text-gray-400">—</span>}</td>
                        <td className="px-3 py-2 text-gray-700 text-xs">
                          {i.header_values?.machine_panel_id || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-700 text-xs">
                          <span className={i.created_by === currentUser?.id ? 'font-medium text-blue-700' : ''}>
                            {i.created_by_name || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{(i.rows || []).length}</td>
                        <td className="px-3 py-2">
                          <Badge className={`${sMeta.color} text-xs font-normal`}>{sMeta.label}</Badge>
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{String(i.created_at || '').slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-0.5">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} title="Export PDF" data-testid={`export-pdf-${i.id}`}>
                                  <Download className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenuItem onClick={(e) => handleExportPDF(i.id, false, false, e)}>
                                  <Download className="h-4 w-4 mr-2" /> {t('auto.iso.colorPdf')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => handleExportPDF(i.id, true, false, e)}>
                                  <Printer className="h-4 w-4 mr-2" /> Print-friendly (B&amp;W)
                                </DropdownMenuItem>
                                <div className="px-2 py-1"><DocLangToggle testId="doc-lang-electricaltestreports-0" /></div>
                                <DropdownMenuItem onClick={(e) => handleExportPDF(i.id, false, true, e)} data-testid={`export-pdf-color-with-photos-${i.id}`}>
                                  <Camera className="h-4 w-4 mr-2" /> {t('auto.iso.colorPhotoEvidence')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => handleExportPDF(i.id, true, true, e)} data-testid={`export-pdf-bw-with-photos-${i.id}`}>
                                  <Camera className="h-4 w-4 mr-2" /> B&amp;W + Photo Evidence
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {can('iso_test_reports.unlock_signed') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); setReassignTarget(i); }}
                                title={t('auto.iso.reassignOwner')}
                                data-testid={`reassign-report-${i.id}`}
                              >
                                <UserCog className="h-3.5 w-3.5 text-amber-600" />
                              </Button>
                            )}
                            {can('iso_test_reports.delete') && (
                              <Button variant="ghost" size="sm" onClick={(e) => handleDelete(i.id, e)} title="Delete" data-testid={`delete-report-${i.id}`}>
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="create-test-report-dialog">
          <DialogHeader>
            <DialogTitle>{t('auto.iso.newElectricalTestReport')}</DialogTitle>
            <DialogDescription>{t('auto.iso.pickATestTypeTemplateAnd')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 min-w-0">
            <div>
              <label className="text-xs font-medium text-gray-600">{t('auto.iso.testType')}</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {Object.entries(TYPE_META).map(([k, m]) => {
                  const Icon = m.icon;
                  const active = createType === k;
                  return (
                    <button
                      key={k}
                      onClick={() => setCreateType(k)}
                      className={`p-2.5 rounded-lg border text-left text-xs transition-all ${active ? `${m.color} ring-2 ring-offset-1` : 'border-gray-200 hover:bg-gray-50'}`}
                      data-testid={`create-type-${k}`}
                    >
                      <Icon className="h-4 w-4 mb-1" />
                      <div className="font-medium">{m.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">{t('auto.iso.template')}</label>
              <Select value={createTplId} onValueChange={setCreateTplId}>
                <SelectTrigger className="w-full" data-testid="create-template-select">
                  <SelectValue placeholder={t('auto.iso.pickATemplate')} />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-3rem)]">
                  {templatesForType.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="text-sm">{t.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {createTplId && (() => {
                const t = templatesForType.find(x => x.id === createTplId);
                if (!t) return null;
                return (
                  <div className="mt-1 text-[11px] text-gray-500 font-mono truncate" title={t.name}>
                    {t.doc_ref} · {(t.columns || []).length} columns
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">{t('auto.iso.projectAutoFillsHeader')}</label>
              <Select value={createProjectId || 'none'} onValueChange={(v) => setCreateProjectId(v === 'none' ? '' : v)}>
                <SelectTrigger className="w-full" data-testid="create-project-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('auto.iso.noProject')}</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={handleCreate} data-testid="confirm-create-report-btn">{t('auto.iso.create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReassignOwnerDialog
        open={!!reassignTarget}
        onOpenChange={(o) => { if (!o) setReassignTarget(null); }}
        instance={reassignTarget}
        kind="test-report"
        onReassigned={() => fetchData()}
      />

      {/* Combined Certificate picker */}
      <Dialog open={!!combinedDialog} onOpenChange={(o) => { if (!o) setCombinedDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="combined-cert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" /> {t('auto.iso.combinedTestCertificate')}
            </DialogTitle>
            <DialogDescription>
              EN 60204-1 deliverable bundling all reports for{' '}
              <span className="font-mono text-gray-700">{combinedDialog?.group?.panel}</span>{' '}
              into one branded PDF.
            </DialogDescription>
          </DialogHeader>
          {combinedDialog && (() => {
            const grp = combinedDialog.group;
            const signedItems = grp.items.filter(i => i.status === 'signed_off' || (i.sign_offs?.length && i.sign_offs.every(s => s.signed)));
            const willInclude = combinedDialog.signedOnly ? signedItems : grp.items;
            return (
              <div className="space-y-3 min-w-0">
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={combinedDialog.signedOnly}
                    onChange={(e) => setCombinedDialog(prev => ({ ...prev, signedOnly: e.target.checked }))}
                    className="mt-0.5"
                    data-testid="combined-signed-only-toggle"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{t('auto.iso.includeOnlySignedReports')}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Only reports with status <strong>{t('auto.iso.signedOff')}</strong> or all sign-offs completed.
                      <span className="ml-1 font-mono text-gray-700">
                        {signedItems.length}/{grp.items.length} qualify.
                      </span>
                    </div>
                  </div>
                </label>
                <div>
                  <label className="text-xs font-medium text-gray-600">{t('auto.iso.outputStyle')}</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setCombinedDialog(prev => ({ ...prev, mono: false }))}
                      className={`p-2.5 rounded-lg border text-sm transition ${!combinedDialog.mono ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' : 'border-gray-200 hover:bg-gray-50'}`}
                      data-testid="combined-color-btn"
                    >
                      <Download className="h-4 w-4 mb-1 mx-auto" />
                      {t('auto.iso.colorPdf')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCombinedDialog(prev => ({ ...prev, mono: true }))}
                      className={`p-2.5 rounded-lg border text-sm transition ${combinedDialog.mono ? 'bg-gray-100 border-gray-400 ring-2 ring-gray-300' : 'border-gray-200 hover:bg-gray-50'}`}
                      data-testid="combined-bw-btn"
                    >
                      <Printer className="h-4 w-4 mb-1 mx-auto" />
                      Print-friendly (B&amp;W)
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 border">
                  Will include <strong>{willInclude.length}</strong> report{willInclude.length !== 1 ? 's' : ''}
                  {combinedDialog.signedOnly && willInclude.length === 0 && (
                    <span className="text-amber-700 ml-1">{t('auto.iso.noneSignedYetGenerateDisabled')}</span>
                  )}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCombinedDialog(null)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <DocLangToggle testId="doc-lang-combined" className="self-center" />
            <Button
              onClick={async () => {
                await handleCombinedCertificate(combinedDialog.group, { mono: combinedDialog.mono, signedOnly: combinedDialog.signedOnly });
                setCombinedDialog(null);
              }}
              disabled={(() => {
                if (!combinedDialog) return true;
                if (combinedDialog.signedOnly) {
                  const signedItems = combinedDialog.group.items.filter(i => i.status === 'signed_off' || (i.sign_offs?.length && i.sign_offs.every(s => s.signed)));
                  return signedItems.length === 0;
                }
                return false;
              })()}
              data-testid="combined-generate-btn"
            >
              <Download className="h-4 w-4 mr-1" /> {t('auto.iso.generate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commissioning Pack (project-wide) Dialog */}
      <Dialog open={!!packDialog} onOpenChange={(o) => { if (!o) setPackDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="commissioning-pack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-orange-500" /> {t('auto.iso.commissioningPack')}
            </DialogTitle>
            <DialogDescription>
              Project: <span className="font-mono text-gray-700">{packDialog?.project_name}</span>{' '}
              — bundles every electrical test report for this project (Continuity · Insulation · Fault Loop · RCD · Torque) into one signed PDF deliverable.
            </DialogDescription>
          </DialogHeader>
          {packDialog && (() => {
            const projItems = filtered.filter(i => i.project_id === packDialog.project_id);
            const signedItems = projItems.filter(i => i.status === 'signed_off' || (i.sign_offs?.length && i.sign_offs.every(s => s.signed)));
            const willInclude = packDialog.signedOnly ? signedItems : projItems;
            const hasTorque = projItems.some(i => i.test_type === 'torque');
            const typeCounts = ['continuity', 'insulation', 'fault_loop', 'rcd', 'torque'].reduce((acc, t) => {
              acc[t] = projItems.filter(i => i.test_type === t).length; return acc;
            }, {});
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-5 gap-2 text-center text-xs">
                  {[
                    ['EC', 'continuity', 'bg-blue-50 text-blue-700'],
                    ['IR', 'insulation', 'bg-purple-50 text-purple-700'],
                    ['FL', 'fault_loop', 'bg-orange-50 text-orange-700'],
                    ['RCD', 'rcd', 'bg-red-50 text-red-700'],
                    ['TQ', 'torque', 'bg-amber-50 text-amber-700'],
                  ].map(([label, k, cls]) => (
                    <div key={k} className={`rounded p-2 ${cls}`} data-testid={`pack-count-${k}`}>
                      <div className="text-base font-semibold">{typeCounts[k]}</div>
                      <div className="text-[10px] opacity-80">{label}</div>
                    </div>
                  ))}
                </div>

                <label className="flex items-start gap-2 text-sm cursor-pointer p-2.5 rounded-lg border border-blue-200 bg-blue-50/50 hover:bg-blue-50 transition">
                  <input
                    type="checkbox"
                    checked={packDialog.handover}
                    onChange={(e) => setPackDialog(prev => ({ ...prev, handover: e.target.checked  }))}
                    className="h-4 w-4 mt-0.5"
                    data-testid="pack-handover-toggle"
                  />
                  <span>
                    <span className="font-medium text-blue-900">{t('auto.iso.customerHandoverDeclarationOfConformity')}</span>
                    <span className="block text-xs text-blue-800/80 mt-0.5">Adds a CE / EN 60204-1 cover page with signature blocks for Test Engineer, {(projects.find(p => p.id === packDialog.project_id)?.electrical_supervisor?.position) || 'Electrical Supervisor'} and Customer Acceptance.</span>
                  </span>
                </label>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={packDialog.signedOnly}
                    onChange={(e) => setPackDialog(prev => ({ ...prev, signedOnly: e.target.checked  }))}
                    className="h-4 w-4"
                    data-testid="pack-signed-only-toggle"
                  />
                  Signed reports only ({signedItems.length} of {projItems.length})
                </label>

                {hasTorque && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={packDialog.includeAppendix}
                      onChange={(e) => setPackDialog(prev => ({ ...prev, includeAppendix: e.target.checked  }))}
                      className="h-4 w-4"
                      data-testid="pack-appendix-toggle"
                    />
                    Append torque reference table &amp; normative references (per BS 7671 / NFPA 70B)
                  </label>
                )}

                <div>
                  <div className="text-xs text-gray-500 mb-1">{t('auto.iso.outputStyle')}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPackDialog(prev => ({ ...prev, mono: false  }))}
                      className={`p-2.5 rounded-lg border text-sm transition ${!packDialog.mono ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' : 'border-gray-200 hover:bg-gray-50'}`}
                      data-testid="pack-color-btn"
                    >
                      {t('auto.iso.color')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPackDialog(prev => ({ ...prev, mono: true  }))}
                      className={`p-2.5 rounded-lg border text-sm transition ${packDialog.mono ? 'bg-gray-100 border-gray-400 ring-2 ring-gray-300' : 'border-gray-200 hover:bg-gray-50'}`}
                      data-testid="pack-bw-btn"
                    >
                      Black &amp; White
                    </button>
                  </div>
                </div>

                <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
                  Will include <strong>{willInclude.length}</strong> report{willInclude.length !== 1 ? 's' : ''}
                  {packDialog.signedOnly && willInclude.length === 0 && (
                    <span className="text-amber-700 ml-1">{t('auto.iso.noneSignedYetGenerateDisabled')}</span>
                  )}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPackDialog(null)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button
              onClick={async () => {
                await handleCommissioningPack(packDialog);
                setPackDialog(null);
              }}
              disabled={(() => {
                if (!packDialog) return true;
                const projItems = filtered.filter(i => i.project_id === packDialog.project_id);
                if (packDialog.signedOnly) {
                  const signedItems = projItems.filter(i => i.status === 'signed_off' || (i.sign_offs?.length && i.sign_offs.every(s => s.signed)));
                  return signedItems.length === 0;
                }
                return projItems.length === 0;
              })()}
              data-testid="pack-generate-btn"
            >
              <Download className="h-4 w-4 mr-1" /> {t('auto.iso.generatePack')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ElectricalTestReports;
